import { useRouter, useSearchParams, usePathname } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';

interface UseDialogUrlStateProps {
  paramName: string;
  paramValue: string;
}

export function useDialogUrlState({ paramName, paramValue }: UseDialogUrlStateProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const [isOpen, setIsOpen] = useState(false);

  // Check URL params on mount and when search params change
  useEffect(() => {
    if (searchParams?.get(paramName) === paramValue) {
      setIsOpen(true);
    }
  }, [searchParams, paramName, paramValue]);

  const updateUrl = useCallback(
    (open: boolean) => {
      if (!router || !searchParams) return;

      setIsOpen(open);
      const newSearchParams = new URLSearchParams(searchParams.toString());

      if (open) {
        newSearchParams.set(paramName, paramValue);
      } else {
        newSearchParams.delete(paramName);
      }

      const newUrl = `${pathname}?${newSearchParams.toString()}`;

      void router.replace(newUrl);
    },
    [router, searchParams, pathname, paramName, paramValue]
  );

  // Clean up URL when component unmounts
  useEffect(() => {
    return () => {
      // Only clean up if router exists and the param matches this hook's instance
      if (router && searchParams && searchParams.get(paramName) === paramValue) {
        const newSearchParams = new URLSearchParams(searchParams.toString());
        newSearchParams.delete(paramName);
        const newUrl = `${pathname}?${newSearchParams.toString()}`;
        void router.replace(newUrl);
      }
    };
  }, [router, searchParams, pathname, paramName, paramValue]);

  return { isOpen, updateUrl };
}
